import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  Bell,
  Bug,
  Boxes,
  Building2,
  CalendarClock,
  ChevronsLeft,
  ChevronsRight,
  DatabaseBackup,
  Globe,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  ListTodo,
  Lock,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Network,
  Phone,
  Printer,
  Radar,
  Plug,
  Router as RouterIcon,
  Search,
  Server,
  FileText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Shield,
  Sun,
  Wallet,
} from 'lucide-react';
import { isCustomerRole, type Permission } from '@itr/shared';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme/ThemeProvider';
import { useRealtime, useRealtimeEvent, useThrottledReload } from '../api/realtime';
import { api, tokens } from '../api/client';
import { useAlertSound, type AlertTrigger } from '../notifications/useAlertSound';
import { CommandPalette } from './CommandPalette';
import { ConnectionLostDialog } from './ConnectionLostDialog';
import { DemoBanner } from './DemoBanner';
import { ErrorBoundary, ReachDot } from './ui';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof Activity;
  permission?: Permission;
  badge?: 'alerts' | 'tickets' | 'tasks' | 'invoices';
  /** Hidden from client-access roles regardless of permission — for pages
   *  that show provider-side catalog data (pricing plans, invoice templates)
   *  a client should never browse, even read-only. */
  staffOnly?: boolean;
  /** For a client-access role, send this item to their own client page
   *  instead — used for "Billing", which otherwise lands a client on the
   *  prepaid-wallet accounts list (almost always empty) instead of where
   *  their actual invoices/subscriptions/utility bills live. */
  clientOwnPage?: boolean;
}

const NAV: Array<{ label: string | null; items: NavEntry[] }> = [
  {
    label: null,
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/alerts', label: 'Alerts', icon: Bell, permission: 'alert:read', badge: 'alerts' },
      { to: '/monitoring', label: 'Monitoring', icon: Activity, permission: 'metric:read' },
      { to: '/security', label: 'Security', icon: ShieldAlert, permission: 'resource:read' },
      { to: '/clients', label: 'Clients', icon: Building2, permission: 'resource:read' },
      { to: '/resources', label: 'All resources', icon: Boxes, permission: 'resource:read' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { to: '/r/servers', label: 'Servers', icon: Server, permission: 'resource:read' },
      { to: '/r/computers', label: 'Computers', icon: Monitor, permission: 'resource:read' },
      { to: '/r/firewalls', label: 'Firewalls', icon: Shield, permission: 'resource:read' },
      { to: '/r/routers', label: 'Routers', icon: RouterIcon, permission: 'resource:read' },
      { to: '/r/networks', label: 'Networks', icon: Network, permission: 'resource:read' },
      { to: '/r/internet-lines', label: 'Internet lines', icon: Globe, permission: 'resource:read' },
      { to: '/r/vpn', label: 'VPN', icon: Lock, permission: 'resource:read' },
      { to: '/r/voip', label: 'VOIP', icon: Phone, permission: 'resource:read' },
      { to: '/r/office-devices', label: 'Office & IoT', icon: Printer, permission: 'resource:read' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/tickets', label: 'Service desk', icon: LifeBuoy, permission: 'ticket:read', badge: 'tickets' },
      { to: '/bugs', label: 'Bug reports', icon: Bug, permission: 'ticket:read' },
      { to: '/tasks', label: 'Tasks', icon: ListTodo, permission: 'task:read', badge: 'tasks' },
      { to: '/tools', label: 'Diagnostics', icon: Radar, permission: 'tools:run', staffOnly: true },
      { to: '/backups', label: 'Backups', icon: DatabaseBackup, permission: 'backup:read' },
      { to: '/vault', label: 'Password safe', icon: KeyRound, permission: 'vault:read' },
      { to: '/billing', label: 'Billing', icon: Wallet, permission: 'billing:read', clientOwnPage: true },
      { to: '/invoices', label: 'Invoices', icon: FileText, permission: 'billing:read', badge: 'invoices', staffOnly: true },
      { to: '/utility-bills', label: 'Utility bills', icon: CalendarClock, permission: 'billing:read' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/plugins', label: 'Plugins', icon: Plug, permission: 'plugin:read' },
      { to: '/access', label: 'Access control', icon: ShieldCheck, permission: 'user:read' },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

export function Shell() {
  const { user, logout, can } = useAuth();
  const { theme, resolvedMode, setMode, preference } = useTheme();
  const { connected } = useRealtime();
  const location = useLocation();
  const navigate = useNavigate();

  // 'default' marks the first entry this app saw, so -1 would leave the app on
  // a deep link or fresh tab; fall back to the parent route there instead.
  const hasHistory = location.key !== 'default';
  const parentPath = location.pathname.replace(/\/[^/]+\/?$/, '') || '/';
  const showBack = hasHistory || location.pathname !== '/';
  const goBack = () => (hasHistory ? navigate(-1) : navigate(parentPath));

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('itr.sidebar') === 'collapsed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [counts, setCounts] = useState({ alerts: 0, tickets: 0, tasks: 0, invoices: 0 });
  const [triggers, setTriggers] = useState<AlertTrigger[]>([]);
  const [bellOpen, setBellOpen] = useState(false);

  const onAlertTrigger = useCallback((trigger: AlertTrigger) => {
    setTriggers((prev) => [trigger, ...prev.filter((t) => t.resourceId !== trigger.resourceId)].slice(0, 20));
  }, []);
  useAlertSound(onAlertTrigger);

  const goToTrigger = (trigger: AlertTrigger) => {
    setTriggers((prev) => prev.filter((t) => t.resourceId !== trigger.resourceId));
    setBellOpen(false);
    navigate(`/resources/${trigger.resourceId}`);
  };

  // A recovery is good news: the bell turns green when nothing waiting on it is still down.
  const bellOk = triggers.length > 0 && triggers.every((t) => t.health === 'up');

  const onBellClick = () => {
    if (triggers.length === 0) return;
    if (triggers.length === 1) {
      goToTrigger(triggers[0]);
      return;
    }
    setBellOpen((v) => !v);
  };

  useEffect(() => {
    localStorage.setItem('itr.sidebar', collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  // Close the mobile drawer on navigation, or it stays open over the new page.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      // "/" jumps to search, but not while the user is typing in a field.
      if (e.key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const loadCounts = useMemo(
    () => async () => {
      try {
        const [alerts, tickets, tasks, billing] = await Promise.all([
          can('alert:read')
            ? api.get<{ firing: { critical: number; warning: number } }>('/api/alerts/summary')
            : Promise.resolve(null),
          can('ticket:read') ? api.get<{ open: number }>('/api/tickets/summary') : Promise.resolve(null),
          can('task:read') ? api.get<{ open: number }>('/api/tasks/summary', { userId: user?.id }) : Promise.resolve(null),
          can('billing:read')
            ? api.get<{ invoices: { dueCount: number } }>('/api/billing/subscriptions/summary')
            : Promise.resolve(null),
        ]);
        setCounts({
          // Only critical + warning earn a badge; info-level noise in the
          // sidebar trains people to ignore the badge entirely. Firing only —
          // an acknowledged alert is already on someone's plate, so
          // acknowledging has to take it off the badge.
          alerts: (alerts?.firing.critical ?? 0) + (alerts?.firing.warning ?? 0),
          tickets: tickets?.open ?? 0,
          // Scoped to the signed-in user — the badge is "what's on my plate",
          // not the whole team's open queue.
          tasks: tasks?.open ?? 0,
          // Due + overdue only — a draft nobody has issued yet, or a paid
          // invoice, is not something the badge should nag about.
          invoices: billing?.invoices.dueCount ?? 0,
        });
      } catch {
        /* the badge is decoration; never break the shell over it */
      }
    },
    [can, user?.id],
  );

  useEffect(() => {
    void loadCounts();
    const timer = window.setInterval(() => void loadCounts(), 60_000);
    return () => window.clearInterval(timer);
  }, [loadCounts]);

  // Throttled: `alert.updated` also arrives on every repeat of a standing
  // alert, and an acknowledge-all sends one per alert — unthrottled, each
  // would cost four requests and the replies could land out of order.
  const reloadCounts = useThrottledReload(() => void loadCounts(), 2000);

  useRealtimeEvent(['alerts', 'tickets', 'tasks'], (event) => {
    if (
      event.type === 'alert.fired' ||
      event.type === 'alert.resolved' ||
      // Acknowledge and suppress both arrive as `alert.updated`.
      event.type === 'alert.updated' ||
      event.type === 'ticket.created' ||
      event.type === 'task.created' ||
      event.type === 'task.updated'
    ) {
      reloadCounts();
    }
  });

  const title = useMemo(() => {
    for (const group of NAV) {
      const match = group.items.find((item) => item.to === location.pathname);
      if (match) return match.label;
    }
    if (location.pathname.startsWith('/resources/')) return 'Resource';
    if (location.pathname.startsWith('/alerts/')) return 'Alert';
    if (location.pathname.startsWith('/tickets/')) return 'Ticket';
    if (location.pathname.startsWith('/tasks/')) return 'Task';
    if (location.pathname.startsWith('/tools')) return 'Diagnostics';
    if (location.pathname.startsWith('/invoices')) return 'Invoices';
    if (location.pathname.startsWith('/access')) return 'Access control';
    if (location.pathname.startsWith('/vault/')) return 'Credential';
    return theme.branding.productName;
  }, [location.pathname, theme.branding.productName]);

  const initials = theme.branding.productName
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <div className="shell">
      {mobileOpen && <div className="overlay" onClick={() => setMobileOpen(false)} style={{ zIndex: 39 }} />}

      <nav className="sidebar" data-collapsed={collapsed} data-open={mobileOpen} aria-label="Main navigation">
        <div className="sidebar-brand">
          {theme.branding.logoUrl ? (
            <img
              src={resolvedMode === 'dark' ? (theme.branding.logoDarkUrl ?? theme.branding.logoUrl) : theme.branding.logoUrl}
              alt=""
              style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: 'var(--itr-radius-sm)' }}
            />
          ) : (
            <span className="sidebar-brand-mark">{initials || 'IT'}</span>
          )}
          {!collapsed && <span className="sidebar-brand-name">{theme.branding.productName}</span>}
        </div>

        <div className="sidebar-nav">
          {NAV.map((group, gi) => {
            const items = group.items.filter(
              (item) => (!item.permission || can(item.permission)) && (!item.staffOnly || !isCustomerRole(user?.role)),
            );
            if (items.length === 0) return null;
            return (
              <div className="nav-section" key={gi}>
                {group.label && !collapsed && <div className="nav-section-label">{group.label}</div>}
                {items.map((item) => {
                  const badge = item.badge ? counts[item.badge] : 0;
                  const to = item.clientOwnPage && isCustomerRole(user?.role) && user?.customerId ? `/clients/${user.customerId}#billing-section` : item.to;
                  return (
                    <NavLink
                      key={item.to}
                      to={to}
                      end={item.to === '/'}
                      className="nav-item"
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon size={16} strokeWidth={1.9} />
                      {!collapsed && <span className="nav-item-label">{item.label}</span>}
                      {!collapsed && badge > 0 && <span className="nav-item-badge">{badge > 99 ? '99+' : badge}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div style={{ padding: 8, borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
          <button
            className="nav-item"
            onClick={() => setCollapsed((v) => !v)}
            style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            {!collapsed && <span className="nav-item-label">Collapse</span>}
          </button>
        </div>
      </nav>

      <div className="main">
        <DemoBanner />
        {user?.impersonatedBy && (
          <div
            role="note"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '4px 12px',
              padding: '6px 14px',
              fontSize: 12.5,
              lineHeight: 1.35,
              textAlign: 'center',
              color: 'var(--itr-text)',
              background: 'color-mix(in srgb, var(--itr-warning) 16%, var(--itr-surface))',
              borderBottom: 'var(--itr-border-w) solid color-mix(in srgb, var(--itr-warning) 40%, transparent)',
            }}
          >
            <span>
              Connected as <strong>{user.name}</strong> ({user.role}) to test this role. Everything you do here is done as them and logged under your name, {user.impersonatedBy.name}.
            </span>
            <button
              className="btn btn-sm"
              onClick={() => {
                tokens.returnToSelf();
                window.location.assign('/settings');
              }}
            >
              Return to {user.impersonatedBy.name}
            </button>
          </div>
        )}
        <header className="topbar">
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            style={{ display: 'none' }}
            data-mobile-only
          >
            <Menu size={17} />
          </button>

          {showBack && (
            <button className="btn btn-ghost btn-icon" onClick={goBack} title="Back" aria-label="Back">
              <ArrowLeft size={17} />
            </button>
          )}

          <span className="topbar-title">{title}</span>

          {/* A silent socket means stale numbers, which is worse than none —
              so the disconnected state is always visible, not hidden. */}
          {!connected && (
            <span className="badge" data-intent="warn" title="Live updates are reconnecting">
              <Activity size={11} /> reconnecting
            </span>
          )}

          <span className="topbar-spacer" />

          <button className="btn btn-sm" onClick={() => setPaletteOpen(true)} style={{ gap: 8 }}>
            <Search size={14} />
            <span style={{ color: 'var(--itr-text-subtle)' }}>Search</span>
            <span className="kbd" style={{ marginLeft: 4 }}>
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
            </span>
          </button>

          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setMode(preference.mode === 'dark' ? 'light' : preference.mode === 'light' ? 'system' : 'dark')}
            title={`Theme: ${preference.mode} (click to change)`}
            aria-label="Toggle colour mode"
          >
            {preference.mode === 'system' ? <Monitor size={16} /> : resolvedMode === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost btn-icon"
              onClick={onBellClick}
              title={triggers.length > 0 ? `${triggers.length} watched-resource alert${triggers.length > 1 ? 's' : ''}` : 'No new alerts'}
              aria-label="Notifications"
            >
              <Bell size={16} className={triggers.length > 0 ? (bellOk ? 'bell-ringing bell-ok' : 'bell-ringing') : undefined} />
              {triggers.length > 0 && <span className={bellOk ? 'bell-count bell-count-ok' : 'bell-count'}>{triggers.length > 9 ? '9+' : triggers.length}</span>}
            </button>
            {bellOpen && triggers.length > 1 && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setBellOpen(false)} />
                <div className="bell-panel">
                  {triggers.map((t) => (
                    <button key={t.resourceId} className="bell-item" onClick={() => goToTrigger(t)}>
                      <span style={{ fontSize: 13, fontWeight: 570 }}>
                        <ReachDot health={t.health} />
                        {t.name}
                      </span>
                      {(t.customerName || t.siteName) && (
                        <span className="subtle" style={{ fontSize: 11.5 }}>
                          {[t.customerName, t.siteName].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span className="subtle" style={{ fontSize: 11.5, textTransform: 'capitalize' }}>
                        {t.previousHealth} → {t.health}
                      </span>
                    </button>
                  ))}
                  <button
                    className="bell-item"
                    style={{ textAlign: 'center', color: 'var(--itr-text-subtle)', fontSize: 12 }}
                    onClick={() => {
                      setTriggers([]);
                      setBellOpen(false);
                    }}
                  >
                    Clear all
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="row" style={{ gap: 8, paddingLeft: 6, borderLeft: 'var(--itr-border-w) solid var(--itr-border)' }}>
            <div style={{ textAlign: 'right', lineHeight: 1.25 }}>
              <div style={{ fontSize: 12.5, fontWeight: 570 }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: 'var(--itr-text-subtle)', textTransform: 'capitalize' }}>{user?.role}</div>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={() => void logout()} title="Sign out" aria-label="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="content">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <ConnectionLostDialog />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} nav={NAV.flatMap((g) => g.items)} />

      <style>{`
        @media (max-width: 860px) { [data-mobile-only] { display: inline-flex !important; } }
        .spin { animation: spin 0.7s linear infinite; }
      `}</style>
    </div>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable;
}
